/* eslint-disable complexity */
import {useRef, useState, useCallback, useEffect} from 'react';

import * as store from '@/store';
import Icon from '@/components/Icon';
import {WarningSvg3} from '@/icons';

import styles from './index.module.scss';

const cardSpace = 4;
const cardMargin = 8;

export interface IConflictTodo {
    id: string;
    todoId: string;
    todo: any;
    conflictUser: {
        nickname: string;
        userGuid: string;
    };
    confirm: () => Promise<void>;
    cancel: () => void;
    closeToast: () => void;
}

interface IProps {
    target: HTMLElement;
    todoData: IConflictTodo;
}

const TodoConflictPopper: React.FC<IProps> = ({
    target,
    todoData,
}) => {
    const close = useCallback(() => {
        todoData.cancel && todoData.cancel();
        const conflictList: IConflictTodo[] =
            store.getData(store.KEYS.TODO_CONFLICT_LIST) || [];
        store.setTodoConflictList(
            conflictList.filter(item => item.id !== todoData.id)
        );
    }, [todoData]);

    const onConfirm = useCallback(() => {
        todoData.confirm &&
            todoData.confirm().then(() => {
                close();
            });
    }, [todoData, close]);

    const wrapRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState({top: 0, left: 0});
    useEffect(() => {
        const scrollContainer = target.closest('.mp-layout-content-container');
        const positionContainer = target.closest('.mp-editor-container-outer');
        const innerEl = target.querySelector('.mp-online-todo-list');
        if (
            !scrollContainer ||
            !positionContainer ||
            !innerEl ||
            !wrapRef.current
        ) {
            return;
        }

        let top = target.offsetTop;
        let parent = target.offsetParent as HTMLElement;
        while (parent && parent !== positionContainer) {
            top += parent.offsetTop;
            parent = parent.offsetParent as HTMLElement;
        }

        const targetHeight = target.offsetHeight;
        const cardHeight = wrapRef.current.offsetHeight;
        const scrollRect = scrollContainer.getBoundingClientRect();
        const posRect = positionContainer.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const innerRect = innerEl.getBoundingClientRect();

        let left = innerRect.left - posRect.left;
        if (left < 0) {
            left = 0;
        }

        // 判断卡片的放置的位置，优先 bottom > top, 如果上下空间都不够则放在下面
        const cardTotalHeight = cardHeight + cardSpace + cardMargin;
        if (
            posRect.bottom - targetRect.bottom >= cardTotalHeight &&
            scrollRect.bottom - targetRect.bottom >= cardTotalHeight
        ) {
            setPosition({top: top + targetHeight + cardSpace, left});
        } else if (targetRect.top - posRect.top >= cardTotalHeight) {
            setPosition({top: top - cardHeight - cardSpace, left});
        } else {
            setPosition({top: top + targetHeight + cardSpace, left});
        }
    }, [target]);

    useEffect(() => {
        if (!wrapRef.current) {
            return;
        }

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    todoData.closeToast();
                    observer.disconnect();
                }
            });
        }, {threshold: [1]});
        observer.observe(wrapRef.current);

        return () => {
            observer.disconnect();
        };
    }, [todoData]);

    return (
        <div ref={wrapRef} className={styles.wrap} style={position}>
            <div className={styles.header}>
                <Icon className={styles.icon} svg={WarningSvg3} noSpace />
                <div className={styles.title}>
                    {todoData.conflictUser.nickname}
                    已将待办修改为以下内容，是否要继续更新？
                    <div>(选择确定将覆盖ta的内容，取消则放弃你的编辑）</div>
                </div>
            </div>
            <div className={styles.body}>
                <div className={styles.left}>
                    <div className={styles.checkbox}></div>
                </div>
                <div className={styles.right}>{todoData.todo.titlePlain}</div>
            </div>
            <div className={styles.footer}>
                <button className={styles.cancelBtn} onClick={close}>
                    取消
                </button>
                <button className={styles.confirmBtn} onClick={onConfirm}>
                    确认
                </button>
            </div>
        </div>
    );
};

interface IListProps {
    todoConflictList?: IConflictTodo[];
}

const TodoConflictPopperList: React.FC<IListProps> = ({
    todoConflictList = [],
}) => {
    return (
        <div data-name="todo-conflict-popper-container">
            {todoConflictList.map(item => {
                const target = document.querySelector<HTMLElement>(
                    `[data-id="${item.id}"][data-online-todo-id="${item.todoId}"]`
                );
                return (
                    target && (
                        <TodoConflictPopper
                            key={item.id}
                            target={target}
                            todoData={item}
                        />
                    )
                );
            })}
        </div>
    );
};

export default store.connect([store.KEYS.TODO_CONFLICT_LIST])(
    TodoConflictPopperList
);
